import React, { useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import spinWheelImage from "../../@shared/assets/spin-wheel.png"; // Update the path as needed

const NUM_SEGMENTS = 10;
const ANGLE_PER_SEGMENT = 360 / NUM_SEGMENTS;
const numbers = Array.from({ length: NUM_SEGMENTS }, (_, i) => i + 1);

const SpinWheel = ({ numberSpun, setNumberSpun }) => {
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [wheelSize, setwheelSize] = useState(200);
  const wheelControls = useAnimation();
  const glowControls = useAnimation();

  // Update wheel size based on window width (80% of window width)
  useEffect(() => {
    const updatewheelSize = () => {
      setwheelSize(window.innerHeight * 0.35);
    };
    updatewheelSize();
    window.addEventListener("resize", updatewheelSize);
    return () => window.removeEventListener("resize", updatewheelSize);
  }, []);

  const spinWheel = async () => {
    if (spinning) return;
    const spinDuration = Math.random() * 3000 + 3000; // in ms
    const fullSpins = Math.floor(Math.random() * 3) + 5;
    const finalIndex = Math.floor(Math.random() * NUM_SEGMENTS);
    const finalAngle = -(
      fullSpins * 360 +
      finalIndex * ANGLE_PER_SEGMENT -
      270
    );
    setSpinning(true);

    // Animate the wheel rotation
    await wheelControls.start({
      rotate: finalAngle,
      transition: { duration: spinDuration / 1000, ease: "easeInOut" },
    });

    setSpinning(false);
    setNumberSpun(numbers[finalIndex]);

    // Start the glow pulsing animation loop
    glowControls.start({
      scale: [1, 1.6, 1],
      opacity: [0.4, 0.8, 0.4],
      transition: { duration: 1.2, ease: "easeInOut", repeat: Infinity },
    });
  };

  // Calculate positions for overlay numbers around the wheel
  const getCoords = (index) => {
    const angle = ((2 * Math.PI) / NUM_SEGMENTS) * index;
    const radius = wheelSize / 3;
    const x = Math.cos(angle) * radius + wheelSize / 2 - 20;
    const y = Math.sin(angle) * radius + wheelSize / 2 - 20;
    return { left: x, top: y };
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: 10,
        position: "relative",
      }}
    >
      {/* 🔻 Pointer */}
      <Box sx={{ position: "absolute", top: 70, zIndex: 100 }}>
        <Box
          sx={{
            width: 0,
            height: 0,
            borderLeft: "30px solid transparent",
            borderRight: "30px solid transparent",
            borderTop: "30px solid #ff4757",
            borderTopRightRadius: 10,
            borderTopLeftRadius: 10,
            borderBottomRadius: 10,
          }}
        />
      </Box>

      {/* 🎯 Wheel */}
      <Box
        sx={{
          position: "relative",
          width: wheelSize,
          height: wheelSize,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <motion.div
          style={{
            width: wheelSize,
            height: wheelSize,
            borderRadius: wheelSize / 2,
            border: "6px solid #2d3436",
            overflow: "hidden",
            position: "absolute",
          }}
          animate={wheelControls}
        >
          <Box
            component="img"
            src={spinWheelImage}
            alt="Spin Wheel"
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: wheelSize / 2,
              transform: "rotate(90deg)",
            }}
          />
          {/* 🔢 Overlay Numbers */}
          {numbers.map((num, index) => {
            const { left, top } = getCoords(index);
            const rotation = ANGLE_PER_SEGMENT * index + 90;
            return (
              <Box
                key={`num-${index}`}
                sx={{
                  position: "absolute",
                  width: 30,
                  height: 30,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  left,
                  top,
                  transform: `rotate(${rotation}deg)`,
                }}
              >
                <Typography
                  sx={{ fontSize: 18, fontWeight: "bold", color: "#2d3436" }}
                >
                  {num}
                </Typography>
              </Box>
            );
          })}
        </motion.div>

        {/* ✨ Glow Behind Selected Number */}
        {selectedNumber !== null && !spinning && (
          <motion.div
            animate={glowControls}
            style={{
              position: "absolute",
              width: 50,
              height: 50,
              borderRadius: 25,
              backgroundColor: "#FFD700",
              zIndex: 1,
              left: wheelSize / 2 - 25, // Centered horizontally on the wheel
              top: 10, // Matches the original placement
            }}
          />
        )}

        {/* 🟢 Start Button */}
        <Button
          onClick={spinWheel}
          disabled={spinning}
          variant="contained"
          sx={{
            position: "absolute",
            width: 100,
            height: 100,
            borderRadius: "50%",
            backgroundColor: "#ff4757",
            color: "#fff",
            zIndex: 10,
            boxShadow: "0px 2px 4px rgba(0,0,0,0.3)",
          }}
        >
          {spinning ? "..." : "START"}
        </Button>
      </Box>
    </Box>
  );
};

export default SpinWheel;
